// pages/WhatWeOfferPage.js
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Head from 'next/head';

import { BackgroundContext, AnimationsActiveContext } from '../../containers/BasePage';
import OfferHero from '../../components/OfferHero';
import OfferIntro from '../../components/OfferIntro';
import OfferSection from '../../components/OfferSection';
import TransitionTextBlock from '../../components/TransitionTextBlock';
import HeroBackground from '../../components/HeroBackground';
import StreamField from '../../components/StreamField';
import Shuffle from '../../components/Shuffle';
import FooterContact from '../../components/FooterContact';
import { basePageWrap } from '../BasePage';

import styles from './WhatWeOfferPage.module.scss';

const WhatWeOfferPage = ({
    gradient,
    streamfield,
    footerContact = {},
    offerHero = {},
    offerIntro = {},
    offerSection = {},
    transitionText = {},
    shuffle = {},
    isDesktop,
    seo,
    title,
}) => {
    const { animationsActive } = useContext(AnimationsActiveContext);

    const scroller = streamfield?.blocksBelowTransition?.find((b) => b.type === 'vertical_scroller');

    const reasons = scroller
        ? scroller.value.items.map((item, i) => ({
              position: i + 1,
              title: item.title,
              text: item.text,
              url: `https://www.stockholmbusinessregion.com${item.href.href}`,
          }))
        : [];

    const itemListSchema = scroller
        ? {
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              name: scroller.value.schemaTitle,
              itemListElement: reasons.map((r) => ({
                  '@type': 'ListItem',
                  position: r.position,
                  name: r.title,
                  description: r.text,
                  url: r.url,
              })),
          }
        : null;

    const whatWeOfferSchema = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: title || '',
        description: offerHero.text || '',
        image: seo?.seoOgImage || '',
        url: seo?.seoOgUrl || '',
        areaServed: {
            '@type': 'Place',
            name: 'Stockholm, Sweden',
        },
    };

    return (
        <>
            <Head>
                {itemListSchema && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(itemListSchema),
                        }}
                    />
                )}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(whatWeOfferSchema),
                    }}
                />
            </Head>

            <div
                className={classNames(styles['Root'], styles[gradient], {
                    [styles['StopAnimations']]: !animationsActive,
                })}>
                <ReduceMotionButton />
                <HeroBackground />
                <OfferHero {...offerHero} />
                <OfferIntro {...offerIntro} isDesktop={isDesktop} />

                <div className={styles['Overflow']}>
                    <OfferSection {...offerSection} />
                    <StreamField blocks={streamfield?.blocks} />

                    {!!shuffle?.item?.title && <Shuffle {...shuffle} />}
                    {!!transitionText?.titleTop && <TransitionTextBlock {...transitionText} />}

                    <StreamField blocks={streamfield?.blocksBelowTransition} />
                    <FooterContact {...footerContact} isDesktop={isDesktop} />
                </div>
            </div>
        </>
    );
};

const ReduceMotionButton = () => {
    const { t } = useTranslation();
    const { whiteBackground } = useContext(BackgroundContext);
    const { animationsActive, toggleAnimationsActive } = useContext(AnimationsActiveContext);

    const wrapperCls = classNames(styles['ReduceMotionButtonWrapper'], {
        [styles['BlackText']]: whiteBackground,
    });
    const iconSrc = `/img/${
        animationsActive
            ? whiteBackground
                ? 'icon-pause-black'
                : 'icon-pause-white'
            : whiteBackground
              ? 'icon-play-black'
              : 'icon-play-white'
    }.svg`;

    return (
        <div className={wrapperCls}>
            <button className={styles['ReduceMotionButton']} onClick={toggleAnimationsActive}>
                <Image
                    className={styles['ReduceMotionIcon']}
                    width={20}
                    height={20}
                    src={iconSrc}
                    alt={animationsActive ? t('Generic.pause') : t('Generic.play')}
                />
                {animationsActive ? t('Insights.reduceMotion') : t('Insights.enableRegularMotion')}
            </button>
        </div>
    );
};

WhatWeOfferPage.propTypes = {
    gradient: PropTypes.string,
    streamfield: PropTypes.shape({
        blocks: PropTypes.array,
        blocksBelowTransition: PropTypes.array,
    }),
    footerContact: PropTypes.object,
    offerHero: PropTypes.object,
    offerIntro: PropTypes.object,
    offerSection: PropTypes.object,
    transitionText: PropTypes.object,
    shuffle: PropTypes.object,
    isDesktop: PropTypes.bool,
};

export default basePageWrap(WhatWeOfferPage);
